import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    TextField,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    ListItemText,
    FormHelperText,
    Stack,
    IconButton,
    InputAdornment
} from "@mui/material";
import { useField, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { UploadFile } from "@mui/icons-material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useSnackbar } from 'notistack';
import DeleteIcon from "@mui/icons-material/Delete";
import ChecklistIcon from '@mui/icons-material/Checklist';

let useSnackbarRef;

export function DebFormTextInput({ label, ...props }) {
    const [field, meta] = useField(props);

    return (
        <TextField
            id={`deb-form-text-input-${props.name}`}
            sx={{ width: "100%" }}
            variant="outlined"
            label={label}
            {...props}
            {...field}
            error={meta.touched && meta.error ? true : false}
            helperText={meta.touched && meta.error ? meta.error : props.helperText || " "}
        />
    );
}

export function DebFormPasswordInput({ label, ...props}) {
    const [showPassword, setShowPassword] = useState(false);
    const [field, meta] = useField(props);

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <TextField
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            sx={{m: "10px"}}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                        >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                    </InputAdornment>
                ),
            }}
            {...props}
            {...field}
            error={meta.touched && meta.error ? true : false}
            helperText={meta.touched && meta.error ? meta.error : props.helperText || " "}
        />
    );
}

export function DebFormCheckbox({ label, ...props }) {
    const [field, meta] = useField(props);
    return (
        <FormGroup>
            <FormControlLabel
                control={<Checkbox {...field} {...props} />}
                label={label}
            />
        </FormGroup>
    );
}

export function DebFormSelect({ label, selectOptions, ...props }) {
    const [field, meta] = useField(props);
    const error = meta.touched && meta.error ? true : false;
    return (
        <FormControl fullWidth>
            <InputLabel error={error}>{label}</InputLabel>
            <Select
                id={`deb-from-select-${props.name}`}
                label={label}
                {...field}
                {...props}
                error={error}>
                {selectOptions.map((option) => {
                    return (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    );
                })}
            </Select>
            <FormHelperText error={error}>
                {meta.touched && meta.error ? meta.error : " "}
            </FormHelperText>
        </FormControl>
    );
}

export function DebFormMultiSelect({
    label,
    selectOptions,
    maxHeight,
    width,
    ...props
}) {
    const [field, meta, helpers] = useField(props);
    const error = meta.touched && meta.error ? true : false;
    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: maxHeight || 224,
                width: width || 250,
            },
        },
    };

    const handleSelectAll = () => {
        const allValues = selectOptions.map(option => option.value);
        helpers.setValue(allValues);
    };

    const handleSelectNone = () => {
        helpers.setValue([]);
    };

    return (
        <div>
            <FormControl fullWidth>
                <InputLabel>{label}</InputLabel>
                <Select
                    label={label}
                    id={`deb-from-multi-select-${props.name}`}
                    multiple
                    input={<OutlinedInput label={label}
                                          endAdornment={
                                              <InputAdornment position="end">
                                                  <IconButton onClick={handleSelectAll}>
                                                      <ChecklistIcon sx={{ marginRight: 1 }} />
                                                  </IconButton>
                                                  <IconButton onClick={handleSelectNone}>
                                                      <DeleteIcon />
                                                  </IconButton>
                                              </InputAdornment>
                                          }/>}
                    renderValue={(selected) => {
                        return selected
                            .map((elem) => {
                                const opt = selectOptions.filter(
                                    (option) => option.value === elem
                                );
                                return opt.length > 0 ? opt[0].label : "";
                            })
                            .join(", ");
                    }}
                    MenuProps={MenuProps}
                    {...field}
                    {...props}
                    error={error}>
                    {selectOptions?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            <Checkbox
                                checked={field?.value?.includes(option.value)}
                            />
                            <ListItemText primary={option.label} />
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText error={error}>
                    {meta.touched && meta.error ? meta.error : " "}
                </FormHelperText>
            </FormControl>
        </div>
    );
}

export function DebDatePickerInput({ label, ...props }) {
    const { setFieldValue } = useFormikContext();
    const [field, meta] = useField(props);
    const error = meta.touched && meta.error ? true : false;
    return (
        <FormControl>
            <DatePicker
                label={label}
                {...field}
                {...props}
                renderInput={(params) => <TextField {...params} />}
                selected={(field.value && new Date(field.value)) || null}
                onChange={(val) => {
                    setFieldValue(field.name, val);
                }}
                error={error}
                views={['year', 'month', 'day']}
            />
            <FormHelperText error={error}>
                {meta.touched && meta.error ? meta.error : " "}
            </FormHelperText>
        </FormControl>
    );
}

export function DebFormFilePicker({label, handleFileUploadCallback, ...props}){
    useSnackbarRef = useSnackbar();
    const [field, meta, helper] = useField(props);
    const error = meta.touched && meta.error ? true : false;
    const [fileName, setFileName] = useState("");

    useEffect(() => {
        if (typeof field.value === "string"){
            setFileName(field.value);
        }
    }, [setFileName, field]);

    const handleChange = (e)=> {
        if (e.target.files?.length > 1) {
            useSnackbarRef.enqueueSnackbar("Seleccione solo un archivo", { 
                preventDuplicate: true, 
                variant: 'error'
            } );
            return;
        }
        const file = e.target.files[0];
        helper.setValue(file);
        setFileName(file.name);
    }
    return (
        <FormGroup>

        <Stack direction={"row"} alignItems="center" spacing={1}>
        <TextField
            id={`deb-form-text-input-${props.name}`}
            sx={{ 
                width: "100%",
            }}
            variant="outlined"
            label={label}
            value={fileName}
            {...props}
            inputProps={{readOnly: true}}
            error={meta.touched && meta.error ? true : false}
            />
            <IconButton color="primary" aria-label="upload picture" component="label">
            <input hidden accept="image/*" type="file" onChange={handleChange}/>
            <UploadFile fontSize="large"/>
            </IconButton>

        </Stack>
        <FormHelperText error={error}>
                {meta.touched && meta.error ? meta.error : " "}
        </FormHelperText>
    </FormGroup>
    );
}

/**
 * Listener de cambios para Formik o DebFormModal
 * El Busca el contexto de Formik y le pasa values a onChange
 */

export const DebFormListener = ({ otraCosa }) => {
    const { values } = useFormikContext();
    useEffect(() => {
        otraCosa(values);
    }, [values, otraCosa]);
    return null;
};

export function formBuilder(fields) {
    return (
        <>
            {fields.map((field) => {
                switch (field.type) {
                    case "text":
                        return (
                            <DebFormTextInput
                                key={field.name}
                                label={field.label}
                                name={field.name}
                            />
                        );
                        break;
                    case "checkbox":
                        return (
                            <DebFormCheckbox
                                key={field.name}
                                label={field.label}
                                name={field.name}
                            />
                        );
                        break;
                    case "select":
                        return (
                            <DebFormSelect
                                key={field.name}
                                label={field.label}
                                name={field.name}
                                selectOptions={field.selectOptions}
                            />
                        );
                        break;
                    case "multiSelect":
                        return (
                            <DebFormMultiSelect
                                key={field.name}
                                label={field.label}
                                name={field.name}
                                selectOptions={field.selectOptions}
                            />
                        );
                    case "datePicker":
                        return (
                            <DebDatePickerInput
                                key={field.name}
                                label={field.label}
                                name={field.name}
                                select={field.value}
                            />
                        );
                        break;
                    default:
                        break;
                }
            })}
        </>
    );
}

// const MyTextInput = ({ label, ...props }) => {
//   // useField() returns [formik.getFieldProps(), formik.getFieldMeta()]
//   // which we can spread on <input>. We can use field meta to show an error
//   // message if the field is invalid and it has been touched (i.e. visited)
//   const [field, meta] = useField(props);
//   return (
//     <>
//       <label htmlFor={props.id || props.name}>{label}</label>
//       <input className="text-input" {...field} {...props} />
//       {meta.touched && meta.error ? (
//         <div className="error">{meta.error}</div>
//       ) : null}
//     </>
//   );
